import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ScrollText, Download, Ban, CheckCircle2, HandCoins, Trash2, AlertCircle } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, formatINR, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

const STATUS_STYLE = {
  active: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  inactive: "bg-[#F0E9E9] text-[#5A4A4A] border border-[#DDD0D0]",
};
const EMPTY_FORM = { name: "", mobile: "", address: "", specialisation: "", opening_balance: 0, payment_terms_days: 15, notes: "" };

export default function Karigars() {
  const navigate = useNavigate();
  const [karigars, setKarigars] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ amount: "", note: "" });
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", type: "advance", mode: "cash", reference: "", note: "" });
  const [staff, setStaff] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);

  const load = async (status = statusFilter) => {
    const { data } = await api.get("/karigar/karigars", { params: status !== "all" ? { status } : {} });
    setKarigars(data);
  };
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    load("all");
    api.get("/crm/me").then(({ data }) => setStaff(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const filtered = karigars.filter((k) => !q || (k.name + k.mobile).toLowerCase().includes(q.toLowerCase()));

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (k) => { setEditing(k); setForm({ ...EMPTY_FORM, ...k }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Karigar name is required"); return; }
    try {
      if (editing) { await api.put(`/karigar/karigars/${editing.id}`, form); toast.success("Karigar updated"); }
      else { await api.post("/karigar/karigars", form); toast.success("Karigar added"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const toggleStatus = async (k) => {
    try { await api.put(`/karigar/karigars/${k.id}/status`); toast.success(`${k.name} ${k.status === "active" ? "deactivated" : "activated"}`); load(); }
    catch { toast.error("Failed to update status"); }
  };

  const openLedger = async (k) => {
    setSelected(k); setLedgerOpen(true); setLedger(null);
    try { const { data } = await api.get(`/karigar/karigars/${k.id}/ledger`); setLedger(data); }
    catch { setLedger({ entries: [], total_debit: 0, total_credit: 0, closing_balance: 0 }); }
  };
  const downloadLedger = (k) => downloadBlob(`/karigar/karigars/${k.id}/ledger.pdf`, `Karigar-Ledger-${k.name}.pdf`).catch(() => toast.error("Download failed"));

  const saveAdjustment = async () => {
    if (!adjForm.amount) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/karigar/karigars/${selected.id}/adjustments`, { amount: Number(adjForm.amount), note: adjForm.note });
      toast.success("Adjustment recorded"); setAdjOpen(false); setAdjForm({ amount: "", note: "" });
      openLedger(selected); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add adjustment"); }
  };

  const openPay = (k) => { setSelected(k); setPayForm({ amount: "", type: "advance", mode: "cash", reference: "", note: "" }); setPayOpen(true); };
  const savePayment = async () => {
    if (!payForm.amount) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/karigar/karigars/${selected.id}/payments`, { ...payForm, amount: Number(payForm.amount) });
      toast.success(payForm.type === "advance" ? "Advance recorded" : "Payment recorded");
      setPayOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record"); }
  };

  const doDelete = async () => {
    if (!deleteFor) return;
    try { await api.delete(`/karigar/karigars/${deleteFor.id}`); toast.success("Karigar deleted"); setDeleteFor(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete karigar"); }
  };

  return (
    <AdminShell title="Karigars">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger data-testid="karigars-tab-all" value="all">All</TabsTrigger>
            <TabsTrigger data-testid="karigars-tab-active" value="active">Active</TabsTrigger>
            <TabsTrigger data-testid="karigars-tab-inactive" value="inactive">Inactive</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input data-testid="karigars-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search karigars..." className="pl-8 w-56" />
          </div>
          <Button data-testid="karigar-add-button" onClick={openCreate} className="gap-2"><Plus className="h-4 w-4" /> Add Karigar</Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="karigars-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]">
              <TableHead>Karigar</TableHead><TableHead>Mobile</TableHead><TableHead>Specialisation</TableHead>
              <TableHead className="text-right">Jobs In Progress</TableHead><TableHead className="text-right">Payable</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((k) => (
                <TableRow key={k.id} className="hover:bg-[#F7F0E6]">
                  <TableCell>
                    <div className="font-medium">{k.name}</div>
                    <div className="text-xs text-muted-foreground">{k.karigar_no}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{k.mobile || "-"}</TableCell>
                  <TableCell className="text-sm">{k.specialisation || "-"}</TableCell>
                  <TableCell className="text-right font-mono">{k.jobs_in_progress || 0}</TableCell>
                  <TableCell className="text-right font-mono">{k.outstanding > 0 ? <span className="text-[#7A1F1F]">{formatINR(k.outstanding)}</span> : formatINR(k.outstanding)}</TableCell>
                  <TableCell><Badge className={STATUS_STYLE[k.status]}>{k.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => openPay(k)}><HandCoins className="h-3.5 w-3.5" /> Pay</Button>
                    <Button data-testid="karigar-ledger-button" size="sm" variant="ghost" className="gap-1" onClick={() => openLedger(k)}><ScrollText className="h-3.5 w-3.5" /> Ledger</Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(k)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(k)}>{k.status === "active" ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}</Button>
                    {staff?.is_manager && (
                      <Button data-testid="karigar-delete-button" size="sm" variant="ghost" className="gap-1 text-destructive hover:text-destructive" onClick={() => setDeleteFor(k)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No karigars yet. Add your first karigar to get started.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="karigar-form-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Karigar" : "Add Karigar"}</DialogTitle>
            <DialogDescription>Karigar profile used for job work orders and the payable ledger.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Karigar Name *</Label><Input data-testid="karigar-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Mobile</Label><Input type="tel" inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Specialisation</Label><Input value={form.specialisation} onChange={(e) => setForm({ ...form, specialisation: e.target.value })} placeholder="e.g. Ring making, Bangle setting" className="mt-1" /></div>
            <div><Label className="text-xs">Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Opening Balance (₹)</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="mt-1 font-mono" /></div>
              <div><Label className="text-xs">Payment Terms (days)</Label><Input type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} className="mt-1 font-mono" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button data-testid="karigar-save-button" onClick={save}>{editing ? "Save Changes" : "Add Karigar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger dialog */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent data-testid="karigar-ledger-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center justify-between pr-6 flex-wrap gap-2">
            <span>Karigar Ledger — {selected?.name}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)}>Adjustment</Button>
              {selected && <Button data-testid="karigar-ledger-download-pdf" size="sm" variant="outline" className="gap-1" onClick={() => downloadLedger(selected)}><Download className="h-3.5 w-3.5" /> PDF</Button>}
            </div>
          </DialogTitle></DialogHeader>
          {!ledger ? <p className="text-sm text-muted-foreground py-6 text-center">Loading...</p> : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="rounded-lg bg-[#F7F0E6] border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Total Debit</div><div className="font-mono text-sm">{formatINR(ledger.total_debit)}</div></div>
                <div className="rounded-lg bg-[#F7F0E6] border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Total Credit</div><div className="font-mono text-sm">{formatINR(ledger.total_credit)}</div></div>
                <div className="rounded-lg bg-[#F7F0E6] border border-border p-3 text-center"><div className="text-xs text-muted-foreground">Payable Balance</div><div data-testid="karigar-ledger-closing-balance" className="font-mono text-sm text-[hsl(var(--primary))]">{formatINR(ledger.closing_balance)}</div></div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {ledger.entries.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No transactions yet.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {ledger.entries.map((e, i) => (
                        <TableRow key={`${e.ref || "row"}-${e.type || ""}-${e.date || ""}-${i}`}>
                          <TableCell className="text-xs">{(e.date || "").slice(0, 10)}</TableCell>
                          <TableCell className="text-sm">{e.description}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{e.debit ? formatINR(e.debit) : "-"}</TableCell>
                          <TableCell className="text-right font-mono text-sm text-[#1F5B2E]">{e.credit ? formatINR(e.credit) : "-"}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatINR(e.balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="karigar-adjustment-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Ledger Adjustment</DialogTitle>
            <DialogDescription>Positive amount increases what you owe the karigar (debit); negative reduces it (credit).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Amount (₹)</Label><Input type="number" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} className="mt-1 font-mono" /></div>
            <div><Label className="text-xs">Note</Label><Input value={adjForm.note} onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button><Button onClick={saveAdjustment}>Save Adjustment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="karigar-payment-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle>Record Payment — {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={payForm.type} onValueChange={(v) => setPayForm({ ...payForm, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="advance">Advance</SelectItem><SelectItem value="payment">Payment</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Amount (₹)</Label><Input data-testid="karigar-payment-amount-input" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1 font-mono" /></div>
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={payForm.mode} onValueChange={(v) => setPayForm({ ...payForm, mode: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Note</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button><Button data-testid="karigar-payment-submit" onClick={savePayment}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive" /> Delete karigar — {deleteFor?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the karigar. Karigars with existing job work history cannot be deleted — Deactivate instead to keep the ledger intact.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Karigar</AlertDialogCancel>
            <AlertDialogAction data-testid="karigar-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Delete Karigar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
